/**
 * @file terminal.js
 * @description Logic for the interactive terminal mode.
 * @version 4.0.0 (Command Pattern Refactor)
 */

import { CommandRegistry } from '../../system/terminal/CommandRegistry.js';
import Sanitizer from '../../system/security/sanitizer.js';
import { Utils } from "../../system/utils/utils.js";
import { VFS } from '../../system/vfs/VFS.js';

export class TerminalInterface {
    constructor(kernel) { // ✅ RECEIVE KERNEL
        // ✅ ASSIGN FROM KERNEL
        this.kernel = kernel;
        this.contentManager = this.kernel.get('content');
        this.themeManager = this.kernel.get('theme');
        this.stateManager = this.kernel.get('state');
        this.eventBus = this.kernel.get('eventBus');
        this.aiSearch = this.kernel.get('aiSearch');
        this.dom = {};
        this.history = [];
        this.historyIndex = -1;
        this.isBufferMode = false;
        this.isBufferKeyListenerAttached = false; // To prevent double-attaching
        this.isInitialized = false;
        this.isProcessing = false;
        this.isAwaitingPassword = false;
        this.inputCallback = null;
        this.currentTheme = 'aegis';
        this.vfs = null;
        this.commandRegistry = new CommandRegistry(this.kernel);
        
        this.promptFormats = {
            'aegis': `user@portfolio:{path}$`,
            'briefing': `[user@portfolio {path}]$`,
            'blueprint': `[user@portfolio:{path}]$`
        };
    }

    // ... loadHtml() is unchanged ...
    
    async loadHtml() {
        try {
            const response = await fetch('modules/terminal/terminal.html');
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const html = await response.text();
            
            // ✅ FIX: Load into the GUI's <section id="terminal">
            const targetContainer = document.getElementById('terminal'); 

            if (targetContainer) {
                targetContainer.innerHTML = html;
            } else {
                console.error('Error loading terminal.html: Target container #terminal not found in gui.html.');
            }
        } catch (e) {
            console.error('Error loading terminal.html:', e);
        }
    }

    // ... init() is unchanged ...
    
    init() {
        this.vfs = new VFS(this.contentManager.getVFS(), this.contentManager);
        const terminalRoot = document.getElementById('terminal-container');
        
        this.dom = {
            buffer: document.getElementById('terminal-buffer'),
            bufferContent: document.getElementById('terminal-buffer-content'),
            output: document.getElementById('terminal-output'),
            input: document.getElementById('terminal-input'),
            prompt: document.getElementById('terminal-prompt'),
            root: terminalRoot 
        };

        this.loadHistory();
        this.dom.input.addEventListener('keydown', (e) => this.onInputKeyDown(e));
        this.dom.output.addEventListener('click', (e) => this.handleOutputClick(e));

        if (this.dom.root) {
            this.dom.root.addEventListener('click', (e) => {
                if (e.target.tagName !== 'A' && !window.getSelection().toString()) {
                    this.focus();
                }
            });
        }

        // ✅ ADD THIS: Robust global key listener for buffer mode
        if (!this.isBufferKeyListenerAttached) {
            document.addEventListener('keydown', (e) => this.handleBufferKeyDown(e));
            this.isBufferKeyListenerAttached = true;
        }
        
        // ✅ USE INJECTED DEPENDENCIES
        this.eventBus.on('terminal:focus', () => this.focus());

        this.updatePrompt();
        this.applyTheme(this.themeManager.currentTheme);
        
        this.stateManager.subscribe('theme', (newTheme) => {
            this.applyTheme(newTheme);
        });

        this.welcome();
    }
    
    // ... all helper methods (applyTheme, focus, welcome, onInputKeyDown, etc.) are unchanged ...
    
    applyTheme(themeName) {
        if (!themeName || this.currentTheme === themeName) return;
        this.currentTheme = themeName;
        const root = this.dom.root;
        if (!root) return;
        root.classList.remove('theme-aegis', 'theme-briefing', 'theme-blueprint');
        root.classList.add(`theme-${themeName}`);
        this.updatePrompt();
    }

    /**
     * Enters "Buffer Mode" (simulating 'less' or 'nano')
     * @param {string} htmlContent - The content to display
     * @param {string} statusText - Text for the bottom bar
     */
    enterBufferMode(htmlContent, statusText = "Manual Page - Press 'q' to quit") {
        this.isBufferMode = true;
        
        // 1. Populate Buffer
        this.dom.bufferContent.innerHTML = htmlContent;
        const statusBar = document.getElementById('terminal-buffer-statusbar');
        if(statusBar) statusBar.textContent = statusText;

        // 2. Show Buffer / Hide standard UI
        this.dom.buffer.classList.add('active');
        
        // 3. Hide Input (Focus is now "global" on the buffer)
        this.dom.input.disabled = true;
        
        // 4. Scroll buffer to top
        this.dom.bufferContent.scrollTop = 0;
    }

    /**
     * Exits "Buffer Mode" and restores the prompt
     */
    exitBufferMode() {
        this.isBufferMode = false;
        
        // 1. Hide Buffer
        this.dom.buffer.classList.remove('active');
        
        // 2. Restore Input
        this.dom.input.disabled = false;
        this.focus();
    }

    focus() {
        const terminalSection = document.getElementById('terminal');
        if (terminalSection && terminalSection.classList.contains('active')) {
            this.dom.input.focus();
        }
    }

    // ✅ NEW DEDICATED HANDLER METHOD
    handleBufferKeyDown(e) {
        if (this.isBufferMode && e.key.toLowerCase() === 'q') {
            e.preventDefault();
            this.exitBufferMode();
        }
    }

    /**
     * Handles Tab Autocompletion
     */
    handleTabCompletion() {
        const currentInput = this.dom.input.value;
        const args = currentInput.split(' ');
        const partial = args.pop() || ''; // The last word is what we are completing
        const basePath = args.join(' '); // The command before the partial path
        
        // 1. Get Content of Current Directory
        const currentDir = this.vfs.getCurrentDirectory();
        const node = this.vfs.getNode(currentDir);
        
        if (!node || !node.children) return;

        // 2. Filter matches
        const matches = node.children.filter(child => child.startsWith(partial));

        if (matches.length === 1) {
            // ✅ EXACT MATCH: Auto-fill
            const completion = matches[0];
            // Reconstruct the command line: "base command" + "completed_filename"
            this.dom.input.value = (basePath ? basePath + ' ' : '') + completion;
        } else if (matches.length > 1) {
            // ✅ MULTIPLE MATCHES: Show candidates
            // We write a temporary line showing options, then reprint the prompt/input
            this.writeLine(matches.join('  '), 'text-muted');
            // Note: We don't change the input value, letting the user type more
        }
    }

    welcome() {
        this.dom.output.innerHTML = '';
        let headerText = '';
        let bodyText = '';
        const bodyMutedText = "Type 'help' for commands or 'exit' to return to the GUI home page.";
        switch(this.currentTheme) {
            case 'blueprint':
                headerText = 'GRID:ACTIVE // SYS:ONLINE';
                bodyText = 'Render process attached. Awaiting instruction.';
                break;
            case 'aegis':
            default:
                headerText = 'CYBER OS - SECURE SHELL';
                bodyText = 'Authentication successful.';
                break;
        }
        const bannerHtml = `
            <div class="system-message-header">${headerText}</div>
            <div class="system-message-body">
                <p>${bodyText}</p>
                <p class="text-muted">${bodyMutedText}</p>
            </div>
        `;
        this.writeHTML(bannerHtml);
        this.isInitialized = true;
    }

    async onInputKeyDown(e) {
        if (this.isProcessing && !this.isAwaitingPassword) return;

        if (this.isAwaitingPassword) {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.isProcessing = true; 
                const enteredValue = this.dom.input.value;
                const isPassword = this.dom.input.type === 'password';
                if (!isPassword) {
                    this.dom.input.value = '';
                    this.writeLine(enteredValue);
                } else {
                    this.writeLine(''); 
                    this.dom.input.value = '';
                }
                if(isPassword) this.dom.input.type = 'text';
                this.isAwaitingPassword = false; 
                if(this.inputCallback) {
                    this.inputCallback(enteredValue); 
                    this.inputCallback = null; 
                }
                this.updatePrompt(); 
                this.isProcessing = false; 
            }
            return;
        }

        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                const cmd = this.dom.input.value;
                this.dom.input.value = '';
                this.isProcessing = true;
                await this.processCommand(cmd);
                this.focus();
                break;
            case 'ArrowUp': e.preventDefault(); this.navigateHistory('up'); break;
            case 'ArrowDown': e.preventDefault(); this.navigateHistory('down'); break;
            case 'Tab': 
                e.preventDefault(); // Stop focus change
                this.handleTabCompletion(); 
                break;
        }
    }

    navigateHistory(direction) {
         if (direction === 'up') {
            if (this.historyIndex < this.history.length - 1) {
                this.historyIndex++;
                this.dom.input.value = this.history[this.history.length - 1 - this.historyIndex] || '';
            }
        } else {
            if (this.historyIndex > 0) {
                this.historyIndex--;
                this.dom.input.value = this.history[this.history.length - 1 - this.historyIndex] || '';
            } else {
                this.historyIndex = -1;
                this.dom.input.value = '';
            }
        }
        this.dom.input.selectionStart = this.dom.input.selectionEnd = this.dom.input.value.length;
    }

    updatePrompt() {
        const pathDisplay = this.vfs.getCurrentDirectory().replace('/home/user', '~');
        const format = this.promptFormats[this.currentTheme] || this.promptFormats['aegis'];
        this.dom.prompt.textContent = format.replace('{path}', pathDisplay);

        if (!this.isAwaitingPassword) {
            this.dom.prompt.classList.remove('is-hidden');
        }
    }


    /**
     * ✅ REFACTORED: processCommand
     * Now uses the command map. The giant switch is gone.
     */
    async processCommand(cmd, skipHistory = false) {
        const trimmedCmd = cmd.trim();
        if (trimmedCmd === '') {
             this.isProcessing = false;
             return;
        }

        // ✅ SANITIZE THE INPUT FOR DISPLAY
        const sanitizedCmd = Sanitizer.sanitizeText(trimmedCmd);

        if (!skipHistory) {
            // this.writeHistoryLine(sanitizedCmd);
            if (this.history.length === 0 || this.history[this.history.length - 1] !== trimmedCmd) {
                 this.history.push(trimmedCmd);
            }
            this.saveHistory();
            this.historyIndex = -1;
        }

        const args = trimmedCmd.split(' ');
        const command = args.shift().toLowerCase();
        
        try {
            // 1. Check Registry (New Architecture)
            const commandObj = this.commandRegistry.get(command);

            if (commandObj) {
                // Inject dependencies dynamically
                commandObj.terminal = this;
                commandObj.fileSystem = this.vfs;
                await commandObj.execute(args);
            }  
            else {
                this.writeLine(`bash: command not found: ${command}`, 'text-red');
            }
        } catch (error) {
            console.error(`Error processing command "${command}":`, error);
            this.writeLine("An internal error occurred.", 'text-red');
        } finally {
            this.isProcessing = false; 
            this.scrollToBottom(); 
        }
    }
    
    requestInput(promptText) {
        return new Promise((resolve) => {
            this.write(promptText, 'text-muted');
            this.dom.prompt.classList.add('is-hidden');
            this.dom.input.type = 'text';
            this.isAwaitingPassword = true;
            this.inputCallback = (value) => {
                resolve(value);
            };
            this.focus();
        });
    }

    clearOutput() {
        if (this.dom.output) {
            this.dom.output.innerHTML = '';
        }
    }

    /**
     * Handles clicks on VFS links in the terminal output.
     */
    handleOutputClick(e) {
        const link = e.target.closest('.vfs-link');
        if (link && link.dataset.command) {
            e.preventDefault();
            const command = link.dataset.command;
            
            // Write the command to the output history to show the action taken
            this.writeHistoryLine(command); 

            // Process the command just as if the user typed it
            this.isProcessing = true;
            this.processCommand(command, true); // true = skip history entry
        }
    }

    write(text, className = '') {
        const span = document.createElement('span');
        span.className = `terminal-line ${className}`;
        span.textContent = text;
        this.dom.output.appendChild(span);
        this.scrollToBottom();
    }

    writeLine(text, className = '') {
        const div = document.createElement('div');
        div.className = `terminal-line ${className}`;
        String(text).split('\n').forEach((line, index) => {
            if (index > 0) div.appendChild(document.createElement('br'));
            div.appendChild(document.createTextNode(line));
        });
        this.dom.output.appendChild(div);
        this.scrollToBottom();
    }

    writeHTML(html) {
        const div = document.createElement('div');
        div.innerHTML = html; 
        this.dom.output.appendChild(div);
        this.scrollToBottom();
    }

    writeHistoryLine(cmd) {
        const sanitizedCmd = cmd;
        this.writeHTML(`<div class="terminal-line history"><span class="terminal-prompt">${this.dom.prompt.textContent}</span> ${sanitizedCmd}</div>`);
        this.scrollToBottom();
    }

    scrollToBottom() {
        setTimeout(() => {
            if (this.dom.output) {
                this.dom.output.scrollTop = this.dom.output.scrollHeight;
            }
        }, 0);
    }

    saveHistory() {
        try {
            localStorage.setItem('cyber-os-history', JSON.stringify(this.history.slice(-50)));
        } catch (e) {
            console.warn("Could not save terminal history to localStorage:", e);
        }
    }

    loadHistory() {
        try {
            const historyJson = localStorage.getItem('cyber-os-history');
            if (historyJson) {
                const loadedHistory = JSON.parse(historyJson);
                if (Array.isArray(loadedHistory)) {
                     this.history = loadedHistory;
                }
            }
        } catch (e) {
            console.warn("Could not load terminal history from localStorage:", e);
            this.history = [];
        }
    }
}